import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { getLandingAttribution } from "@/lib/landingAttribution";
import { publicUrl } from "@/lib/canonical";
import { fireAssessmentConversion, fireAssessmentStartedConversion, fireAssessmentCompletedConversion } from "@/lib/openaiPixel";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Linkedin, Link2, Share2 } from "lucide-react";
import { track } from "@/lib/analytics";
import {
  ROLES,
  MATTERS,
  SITUATIONS,
  TIMELINES,
  UNSURE_ID,
  DOMAIN_ORDER,
  MAX_DOMAINS,
  CLARITY_INDEX,
  OPEN_FACT_PATTERN,
  OPEN_LOOP,
  computeTriage,
} from "@/data/clarityIndex";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/* ============================================================ */

const StepLabel = ({ n, text }) => (
  <div className="mb-5">
    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-blueprint">Step {n} of 4</div>
    <div className="font-serif text-2xl sm:text-3xl tracking-tight font-semibold mt-1.5 leading-snug">{text}</div>
  </div>
);

const OptionButton = ({ selected, onClick, label, hint, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`w-full text-left px-4 py-3 border transition-colors ${
      selected ? "border-blueprint bg-blueprint text-white" : "border-ink/30 bg-white text-ink hover:border-ink"
    }`}
  >
    <span className="flex items-start justify-between gap-3">
      <span>
        <span className="block font-mono text-xs leading-snug">{label}</span>
        {hint && <span className={`block font-mono text-[10px] mt-0.5 ${selected ? "text-white/70" : "text-ink/55"}`}>{hint}</span>}
      </span>
      {selected && <Check size={14} className="shrink-0 mt-0.5" />}
    </span>
  </button>
);

export default function RiskTriageQuiz({ open, onOpenChange }) {
  const [step, setStep] = useState(0); // 0..3 questions, 4 result
  const [role, setRole] = useState(null);
  const [matter, setMatter] = useState(null);
  const [situations, setSituations] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [lead, setLead] = useState({ first_name: "", email: "", marketing_consent: false });
  const [challenge, setChallenge] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [activation, setActivation] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(0); setRole(null); setMatter(null); setSituations([]); setTimeline(null); setSent(false);
      setRedirecting(false); setActivation(null);
      setLead({ first_name: "", email: "", marketing_consent: false }); setChallenge(null);
      track("quiz_started", { source: "landing_hero" });
    }
  }, [open]);

  // Anti-bot challenge for the optional email capture on the result screen.
  useEffect(() => {
    if (step === 4 && !challenge) {
      api.get("/checklist/challenge").then((r) => setChallenge(r.data)).catch(() => {});
    }
  }, [step, challenge]);

  const result = step === 4 ? computeTriage({ role, matter, situations, timeline }) : null;

  // Fire-and-forget funnel events for admin Assessment Insights. `concerns`
  // keeps its field name (the collection has two years of rows under it);
  // schema_version 2 marks the domain-mapped vocabulary.
  const postTriage = (kind, r, recOverride) =>
    api.post("/triage/event", {
      role, matter, concerns: situations, timeline,
      score: r.score, band: r.band, rec: recOverride || r.rec, kind,
      schema_version: 2, domain_count: r.count,
    }).catch(() => {});

  const canNext = [!!role, !!matter, situations.length > 0, !!timeline][step];
  const next = () => {
    if (step === 0) {
      // Clean "started": answered Q1 and advancing (distinct from the auto-open).
      track("quiz_engaged", { source: "landing_hero" });
      fireAssessmentStartedConversion();
    }
    if (step === 3) {
      const r = computeTriage({ role, matter, situations, timeline });
      track("quiz_completed", { role, matter, situations: situations.length, timeline, domains: r.count, score: r.score, stage: r.stage, rec: r.rec });
      fireAssessmentCompletedConversion();
      postTriage("completed", r);
    }
    setStep((s) => s + 1);
  };

  // "I'm not sure" is exclusive in both directions: it clears the specifics,
  // and picking a specific clears it.
  const toggleSituation = (id) =>
    setSituations((cur) => {
      if (id === UNSURE_ID) return cur.includes(UNSURE_ID) ? [] : [UNSURE_ID];
      const without = cur.filter((x) => x !== UNSURE_ID);
      return without.includes(id) ? without.filter((x) => x !== id) : [...without, id];
    });

  // Sharing sends people to the assessment itself. The three shared-result
  // pages were retired in Feb 2026 (their numbering ran opposite to the
  // Clarity Index), and an invitation is the better ask anyway.
  const [copied, setCopied] = useState(false);
  const shareUrl = publicUrl("/start");
  const shareText = result && !result.open
    ? `The CLARITY Assessment mapped my digital assets scenario across ${result.count} of ${MAX_DOMAINS} regulatory domains. Four questions, about three minutes:`
    : "The CLARITY Assessment is four questions that map which regulatory domains a digital assets scenario puts in play. About three minutes:";
  const shareResult = (channel) => {
    track("quiz_result_share", { channel, rec: result.rec, score: result.score, domains: result.count });
    if (channel === "linkedin") {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        "_blank", "noopener,width=640,height=640",
      );
    } else if (channel === "x") {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
        "_blank", "noopener,width=640,height=640",
      );
    } else if (channel === "native") {
      navigator.share({ title: "The CLARITY Assessment™", text: shareText, url: shareUrl }).catch(() => {});
    } else {
      const text = `${shareText} ${shareUrl}`;
      const markCopied = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
      const fallbackCopy = () => {
        // execCommand path for contexts where the Clipboard API is unavailable
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand("copy"); } catch { ok = false; }
        document.body.removeChild(ta);
        return ok;
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(markCopied).catch(() => {
          if (fallbackCopy()) markCopied();
          else toast.error("Could not copy the link");
        });
      } else if (fallbackCopy()) {
        markCopied();
      } else {
        toast.error("Could not copy the link");
      }
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!challenge) return toast.error("One moment, still preparing the form. Try again.");
    setSending(true);
    try {
      // Landing-page attribution (e.g., /start, /mugenvip): tags the lead
      // record with signup_page + UTM values captured on the landing page.
      const attribution = getLandingAttribution();
      const res = await api.post("/assessment/result-email", {
        email: lead.email,
        first_name: lead.first_name.trim(),
        stage: result.stage,
        score: result.score,
        domain_count: result.count,
        // The answer ids, not the domain names: the server maps them itself so
        // the emailed report always matches the screen.
        situations,
        challenge_token: challenge.challenge_token,
        website: "",
        marketing_consent: !!lead.marketing_consent,
        ...(attribution ? {
          signup_page: attribution.signup_page || undefined,
          utm_source: attribution.utm_source || undefined,
          utm_medium: attribution.utm_medium || undefined,
          utm_campaign: attribution.utm_campaign || undefined,
          utm_source_captured: attribution.utm_source_captured || undefined,
        } : {}),
      });
      setSent(true);
      track("quiz_email_captured", { score: result.score, domains: result.count, rec: result.rec });
      // OpenAI Ads conversion: fires exactly once, only at assessment
      // completion + email submission (never on page load). No customer
      // data is sent to OpenAI.
      fireAssessmentConversion();
      postTriage("lead_captured", result);
      // Context-aware next step (activation pilot). When the server returns an
      // activation payload (flags on), eligible completers see the intro offer
      // or their free-memo link and we DO NOT auto-redirect. Everyone else, and
      // whenever the pilot is off (activation null), keeps the existing
      // six-second redirect to /webinar.
      const act = res?.data?.activation || null;
      setActivation(act);
      const holdForCta = act && (act.cta === "intro" || act.cta === "free");
      if (res?.data?.delivered === true && !holdForCta) {
        setRedirecting(true);
        setTimeout(() => {
          onOpenChange(false);
          window.location.assign("/webinar");
        }, 6000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
      setChallenge(null); // nonce is one-shot; fetch a fresh one for retry
    } finally {
      setSending(false);
    }
  };

  const level = result ? (result.open ? OPEN_FACT_PATTERN : CLARITY_INDEX[result.stage]) : null;

  const shareRow = (
    <div data-testid="quiz-share-row" className="mt-4 border-t border-rule pt-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/60 mb-2">
        Who else in your network owns this risk?
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="quiz-share-linkedin"
          onClick={() => shareResult("linkedin")}
          className="inline-flex items-center gap-1.5 border border-ink bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink hover:bg-ink hover:text-white transition-colors"
        >
          <Linkedin size={13} strokeWidth={1.75} /> LinkedIn
        </button>
        <button
          type="button"
          data-testid="quiz-share-x"
          onClick={() => shareResult("x")}
          className="inline-flex items-center gap-1.5 border border-ink bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink hover:bg-ink hover:text-white transition-colors"
        >
          <span className="font-bold text-[12px] leading-none">𝕏</span> Post
        </button>
        <button
          type="button"
          data-testid="quiz-share-copy"
          onClick={() => shareResult("copy")}
          className={`inline-flex items-center gap-1.5 border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${copied ? "border-[#059669] bg-[#059669] text-white" : "border-ink bg-white text-ink hover:bg-ink hover:text-white"}`}
        >
          {copied ? <Check size={13} strokeWidth={2} /> : <Link2 size={13} strokeWidth={1.75} />}
          {copied ? "Copied" : "Copy link"}
        </button>
        {typeof navigator !== "undefined" && !!navigator.share && (
          <button
            type="button"
            data-testid="quiz-share-native"
            onClick={() => shareResult("native")}
            className="inline-flex items-center gap-1.5 border border-ink bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink hover:bg-ink hover:text-white transition-colors"
          >
            <Share2 size={13} strokeWidth={1.75} /> Share
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="risk-triage-quiz"
        className="max-w-xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto rounded-none border border-ink bg-paper p-6 sm:p-8"
      >
        <DialogTitle className="sr-only">The CLARITY Assessment</DialogTitle>
        {/* Progress rule */}
        <div className="flex gap-1 mb-5" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-blueprint" : "bg-ink/15"}`} />
          ))}
        </div>

        {step === 0 && (
          <div>
            <StepLabel n={1} text="Which seat do you sit in?" />
            <div className="space-y-2">
              {ROLES.map((r) => (
                <OptionButton key={r.id} testid={`quiz-role-${r.id}`} label={r.label} selected={role === r.id} onClick={() => setRole(r.id)} />
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <StepLabel n={2} text="What brought digital assets onto your desk?" />
            <div className="space-y-2">
              {MATTERS.map((m) => (
                <OptionButton key={m.id} testid={`quiz-matter-${m.id}`} label={m.label} hint={m.hint} selected={matter === m.id} onClick={() => setMatter(m.id)} />
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <StepLabel n={3} text="Which of these are true in your situation? Select all that apply." />
            <div className="space-y-2">
              {SITUATIONS.map((s) => (
                <OptionButton
                  key={s.id}
                  testid={`quiz-situation-${s.id}`}
                  label={s.label}
                  selected={situations.includes(s.id)}
                  onClick={() => toggleSituation(s.id)}
                />
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <StepLabel n={4} text="How live is this?" />
            <div className="space-y-2">
              {TIMELINES.map((t) => (
                <OptionButton key={t.id} testid={`quiz-timeline-${t.id}`} label={t.label} selected={timeline === t.id} onClick={() => setTimeline(t.id)} />
              ))}
            </div>
          </div>
        )}

        {step < 4 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              data-testid="quiz-back"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60 hover:text-ink disabled:opacity-0 transition-colors"
            >
              <ArrowLeft size={13} /> Back
            </button>
            <button
              type="button"
              data-testid="quiz-next"
              onClick={next}
              disabled={!canNext}
              className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-ink hover:bg-blueprint hover:border-blueprint disabled:opacity-40 transition-colors"
            >
              {step === 3 ? "See my Clarity Index" : "Continue"} <ArrowRight size={14} />
            </button>
          </div>
        )}

        {step === 4 && result && (
          <div data-testid="quiz-result">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-blueprint">Assessment Findings</div>
            <div data-testid="quiz-clarity-index" className="mt-2 font-serif text-3xl sm:text-4xl tracking-tight font-semibold leading-tight">
              {result.open
                ? "Your Clarity Index: Fact pattern open"
                : `Your Clarity Index: ${level.name} · ${result.count} of ${MAX_DOMAINS} domains touched`}
            </div>
            {/* Breadth meter: one mark per domain, filled for the ones in play.
                Self-explanatory next to the count in the headline, so it needs
                no caption. */}
            {!result.open && (
              <div data-testid="quiz-breadth-meter" className="mt-3 flex gap-1" aria-hidden="true">
                {DOMAIN_ORDER.map((d, i) => (
                  <div key={d} className={`h-2 flex-1 ${i < result.count ? "bg-blueprint" : "bg-ink/10"}`} />
                ))}
              </div>
            )}
            <p data-testid="quiz-level-text" className="mt-4 font-mono text-xs text-ink/85 leading-relaxed">
              {level.text}
            </p>

            {/* Flagged domains: the name, why it was flagged in the visitor's
                own words, then the question a full run would have to answer. */}
            {result.domains.length > 0 && (
              <div data-testid="quiz-flagged-domains" className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/60 mb-2">Flagged domains</div>
                <div className="space-y-2">
                  {result.domains.map((d) => (
                    <div key={d.name} data-testid={`quiz-domain-${slug(d.name)}`} className="border border-ink/30 bg-white p-4">
                      <div className="font-serif text-lg tracking-tight font-semibold leading-snug">{d.name}</div>
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-blueprint">Flagged because:</div>
                      <ul className="mt-1 space-y-1">
                        {d.because.map((b) => (
                          <li key={b} className="font-mono text-[11px] text-ink/75 leading-snug pl-3 border-l border-ink/20">{b}</li>
                        ))}
                      </ul>
                      <p className="mt-2.5 font-mono text-xs text-ink leading-relaxed">{d.question}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p data-testid="quiz-open-loop" className="mt-6 border-l-2 border-blueprint pl-3 font-serif italic text-base leading-relaxed text-ink/85">
              {OPEN_LOOP}
            </p>

            {/* Draws the line between the three things a reader can confuse:
                the free assessment measures breadth, the kit shows a fictional
                example, and only a paid guided intake uses their own facts. */}
            <p data-testid="quiz-index-vs-memo" className="mt-3 font-mono text-xs text-ink/80 leading-relaxed">
              Your Clarity Index maps breadth. An Issue-Mapping Memorandum is built from your facts.
            </p>

            {/* Lead capture: emails the visitor the full report, with the
                Clarity Issue-Mapping Starter Kit attached. */}
            {!sent ? (
              <form onSubmit={submitLead} className="mt-6 border-t border-rule pt-4">
                <div className="font-serif text-xl tracking-tight font-semibold leading-snug mb-2">
                  Get your full Clarity Index report.
                </div>
                <div className="font-mono text-xs text-ink/80 leading-relaxed mb-3">
                  Your screen shows which domains are flagged. Your emailed report includes the
                  complete Issue-Mapping Primer for each one, plus the Clarity Issue-Mapping
                  Starter Kit. Your kit includes two resources to start addressing your
                  highest-priority domains today: the Digital Assets Regulatory Issue-Spotting Checklist and
                  a sample memorandum built from a fictional scenario.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block">
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60 mb-1">First name</span>
                    <input
                      data-testid="quiz-lead-first-name"
                      required
                      maxLength={80}
                      autoComplete="given-name"
                      value={lead.first_name}
                      onChange={(e) => setLead((l) => ({ ...l, first_name: e.target.value }))}
                      placeholder="First name"
                      className="w-full border border-ink/40 bg-white px-3 py-2.5 font-mono text-xs focus:outline-none focus:border-blueprint"
                    />
                  </label>
                  <label className="block">
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60 mb-1">Work email</span>
                    <input
                      data-testid="quiz-lead-email"
                      required
                      type="email"
                      value={lead.email}
                      onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                      placeholder="Work email"
                      className="w-full border border-ink/40 bg-white px-3 py-2.5 font-mono text-xs focus:outline-none focus:border-blueprint"
                    />
                  </label>
                </div>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    data-testid="quiz-marketing-consent"
                    type="checkbox"
                    checked={lead.marketing_consent}
                    onChange={(e) => setLead((l) => ({ ...l, marketing_consent: e.target.checked }))}
                    className="mt-0.5 shrink-0"
                  />
                  <span className="font-mono text-[10px] leading-relaxed text-ink/65">
                    Optional: send me occasional educational emails and relevant offers from Web3 Ready CLARITY. This is not required to get your results or Starter Kit, and you can unsubscribe at any time.
                  </span>
                </label>
                <button
                  data-testid="quiz-lead-submit"
                  type="submit"
                  disabled={sending}
                  className="mt-3 w-full bg-white text-ink py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border border-ink hover:bg-ink hover:text-white disabled:opacity-60 transition-colors"
                >
                  {sending ? "Sending…" : "Email me my report and Starter Kit →"}
                </button>
              </form>
            ) : (
              <div data-testid="quiz-lead-sent" className="mt-6 border-t border-rule pt-4">
                <div className="font-mono text-xs text-[#059669]">
                  Sent. Your full Clarity Index report and the Clarity Issue-Mapping Starter Kit are on the way to your inbox.
                </div>
                {activation && (activation.cta === "intro" || activation.cta === "free") && (
                  <div data-testid="quiz-activation-cta" className="mt-4 border-2 border-blueprint bg-white p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blueprint mb-2">
                      Your next step
                    </div>
                    {activation.cta === "intro" ? (
                      <>
                        <div className="font-serif text-xl tracking-tight text-ink leading-snug">
                          {activation.primary_cta}
                        </div>
                        <p className="mt-1 font-mono text-[11px] text-ink/75">
                          <span className="line-through text-ink/50">${Number(activation.standard_usd).toFixed(0)}</span>{" "}
                          standard, your introductory price ${Number(activation.intro_usd).toFixed(2)}.
                          {activation.expires_label ? ` Offer expires ${activation.expires_label}.` : ""}
                        </p>
                      </>
                    ) : (
                      <div className="font-serif text-xl tracking-tight text-ink leading-snug">
                        {activation.primary_cta}
                      </div>
                    )}
                    <a
                      data-testid="quiz-activation-primary"
                      href={activation.primary_href}
                      className="mt-3 inline-flex items-center gap-2 bg-blueprint text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] border border-blueprint hover:bg-ink hover:border-ink transition-colors"
                    >
                      {activation.primary_cta}
                    </a>
                    <a
                      data-testid="quiz-activation-secondary"
                      href={activation.secondary_href || "/webinar"}
                      className="mt-2 block font-mono text-[11px] text-ink/70 underline underline-offset-2 hover:text-blueprint"
                    >
                      {activation.secondary_cta}
                    </a>
                  </div>
                )}
                {redirecting && (
                  <div data-testid="quiz-lead-redirecting" className="mt-2 font-mono text-xs text-ink/70">
                    Take your results to the next level…
                  </div>
                )}
                {/* Sharing is asked for only once the visitor has committed. */}
                {shareRow}
              </div>
            )}

            <p className="mt-4 font-mono text-[10px] text-ink/55 leading-relaxed">
              The Clarity Index reflects the breadth of regulatory surface your responses describe, for educational and issue-identification purposes only. It is not a measure of legal risk, is not legal advice, and creates no attorney-client relationship.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
